import { BulkReceiveModal } from '@/components/products/BulkReceiveModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useProductActions } from '@/hooks/product/use-product-actions';
import { useProductStockAdjustment } from '@/hooks/product/use-product-stock-adjustment';
import { AdjustInStockDialog } from '@/pages/stock/components/AdjustInStockDialog';
import { AdjustOutStockDialog } from '@/pages/stock/components/AdjustOutStockDialog';
import { ProductVariant, ProductWithVariantsStock } from '@/types/product-type';
import { useState, useEffect } from 'react';
import { useProductContext } from '../context/product-context';
import { calculateProductStockStats } from './product-stock-utils';
import { ProductTreeRowContent } from './product-tree-row-content';
import { VariantStockRow } from './variant-stock-row';

interface ProductTreeRowProps {
  product: ProductWithVariantsStock;
  isLast?: boolean;
  onEditVariant?: (variant: ProductVariant) => void;
  /** When true, forces variants to be hidden regardless of local toggle state */
  isCollapsed?: boolean;
}

export function ProductTreeRow({ product, onEditVariant, isCollapsed = false }: ProductTreeRowProps) {
  const [isOpen, setIsOpen] = useState(!isCollapsed);

  useEffect(() => {
    setIsOpen(!isCollapsed);
  }, [isCollapsed]);

  const { refresh } = useProductContext();

  // Calculate stock stats
  const stats = calculateProductStockStats(product);

  // Product actions (edit, delete, receive stock)
  const {
    showBulkReceiveModal,
    showDeleteConfirm,
    handleEdit,
    handleReceiveStock,
    handleDelete,
    confirmDelete,
    handleBulkReceiveStock,
    setShowBulkReceiveModal,
    setShowDeleteConfirm,
  } = useProductActions({
    productId: product.productId,
    productName: product.productName,
    onSuccess: refresh,
  });

  // Stock adjustment logic
  const {
    adjustInDialogState,
    adjustOutDialogState,
    handleAdjustIn,
    handleAdjustOut,
    setAdjustInDialogState,
    setAdjustOutDialogState,
  } = useProductStockAdjustment();

  const handleEditVariant = (variant: ProductVariant) => {
    if (onEditVariant) {
      onEditVariant(variant);
    }
  }

  return (
    <>
      <ProductTreeRowContent
        product={product}
        stats={stats}
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        onEdit={handleEdit}
        onReceiveStock={handleReceiveStock}
        onDelete={handleDelete}
      />

      {/* Variant Rows */}
      {isOpen &&
        product.variants.map((variant, index) => (

          <VariantStockRow
            key={variant.id}
            variant={variant}
            isLast={index === product.variants.length - 1}
            onEditVariant={handleEditVariant}

            onAdjustIn={handleAdjustIn}
            onAdjustOut={handleAdjustOut}
          />
        ))}

      {/* Bulk Receive Modal */}
      {showBulkReceiveModal && (
        <BulkReceiveModal
          isOpen={showBulkReceiveModal}
          productId={product.productId.toString()}
          onSubmit={handleBulkReceiveStock}
          onCancel={() => setShowBulkReceiveModal(false)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{product.productName}</strong>? This action
              cannot be undone and will permanently remove the product and all its variants.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Adjust In Dialog */}
      {
        adjustInDialogState.selectedStock && (
          <AdjustInStockDialog
            isOpen={adjustInDialogState.isOpen}
            onClose={() => setAdjustInDialogState({ isOpen: false, selectedStock: null, dailyStockId: null })}
            onSuccess={refresh}
            stock={adjustInDialogState.selectedStock}
            sku={adjustInDialogState.selectedStock.sku}
          />
        )
      }

      {/* Adjust Out Dialog */}
      {adjustOutDialogState.selectedStock && (
        <AdjustOutStockDialog
          isOpen={adjustOutDialogState.isOpen}
          onClose={() => setAdjustOutDialogState({ isOpen: false, selectedStock: null, dailyStockId: null })}
          onSuccess={refresh}
          stock={adjustOutDialogState.selectedStock}
          sku={adjustOutDialogState.selectedStock.sku}
        />
      )}
    </>
  );
}
